export interface Uv {
  x: number;
  y: number;
}

export const PACKED_LAYOUTS = ['side-by-side', 'top-bottom', 'alpha-strip-half'] as const;
export type PackedLayout = (typeof PACKED_LAYOUTS)[number];
export const PACKED_GUARD_PX = 32;

export interface PixelRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PackedGeometry {
  layout: PackedLayout;
  sourceWidth: number;
  sourceHeight: number;
  packedWidth: number;
  packedHeight: number;
  guardPx: number;
  colour: PixelRect;
  alpha: PixelRect;
}

export interface NormalizedPackedGeometry {
  layout: PackedLayout;
  colour: PixelRect;
  alpha: PixelRect;
}

export function packedGeometry(
  sourceWidth: number,
  sourceHeight: number,
  layout: PackedLayout,
  guardPx = PACKED_GUARD_PX,
): PackedGeometry {
  const width = Math.max(2, Math.round(sourceWidth));
  const height = Math.max(2, Math.round(sourceHeight));
  const guard = Math.max(16, Math.ceil(guardPx / 16) * 16);
  if (layout === 'side-by-side') {
    return {
      layout, sourceWidth: width, sourceHeight: height,
      packedWidth: width * 2 + guard, packedHeight: height, guardPx: guard,
      colour: { x: 0, y: 0, width, height },
      alpha: { x: width + guard, y: 0, width, height },
    };
  }
  if (layout === 'top-bottom') {
    return {
      layout, sourceWidth: width, sourceHeight: height,
      packedWidth: width, packedHeight: height * 2 + guard, guardPx: guard,
      colour: { x: 0, y: 0, width, height },
      alpha: { x: 0, y: height + guard, width, height },
    };
  }
  // A half-width × half-height mask is tiled into a shallow atlas: its top and
  // bottom halves sit beside one another. The video rectangle therefore grows
  // by about 25%, while the receiver restores an isotropic half-size matte.
  const alphaHalfWidth = Math.max(1, Math.floor(width / 2));
  const alphaTileHeight = Math.max(1, Math.floor(height / 4));
  return {
    layout, sourceWidth: width, sourceHeight: height,
    packedWidth: width, packedHeight: height + guard + alphaTileHeight, guardPx: guard,
    colour: { x: 0, y: 0, width, height },
    alpha: { x: 0, y: height + guard, width: alphaHalfWidth * 2, height: alphaTileHeight },
  };
}

export function normalizePackedGeometry(geometry: PackedGeometry): NormalizedPackedGeometry {
  const normalize = (rect: PixelRect): PixelRect => ({
    x: rect.x / geometry.packedWidth,
    y: rect.y / geometry.packedHeight,
    width: rect.width / geometry.packedWidth,
    height: rect.height / geometry.packedHeight,
  });
  return { layout: geometry.layout, colour: normalize(geometry.colour), alpha: normalize(geometry.alpha) };
}

function rectUv(uv: Uv, rect: PixelRect): Uv {
  return { x: rect.x + uv.x * rect.width, y: rect.y + uv.y * rect.height };
}

export const packedColourUv = (uv: Uv, geometry: NormalizedPackedGeometry): Uv => rectUv(uv, geometry.colour);
export const packedAlphaUv = (uv: Uv, geometry: NormalizedPackedGeometry): Uv => {
  if (geometry.layout !== 'alpha-strip-half') return rectUv(uv, geometry.alpha);
  const atlasUv = uv.y < 0.5
    ? { x: uv.x * 0.5, y: uv.y * 2 }
    : { x: 0.5 + uv.x * 0.5, y: (uv.y - 0.5) * 2 };
  return rectUv(atlasUv, geometry.alpha);
};

export interface NormalizedBox {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface MaskFrame {
  width: number;
  height: number;
  categories: Uint8Array;
  confidence?: Float32Array;
}

export interface AlphaEdgeMeasurement {
  layout: PackedLayout;
  encodedAlphaPixels: number;
  edgePixels: number;
  edgeMeanAbsoluteError: number;
  edgePsnrDb: number;
}

export function softAlphaBytes(mask: MaskFrame): Uint8ClampedArray {
  const pixels = mask.width * mask.height;
  if (mask.width <= 0 || mask.height <= 0
    || mask.categories.length !== pixels
    || mask.confidence?.length !== pixels) return new Uint8ClampedArray(pixels);
  return Uint8ClampedArray.from(mask.confidence, (confidence, index) => {
    if (mask.categories[index] === 0 || !Number.isFinite(confidence)) return 0;
    return Math.round(Math.max(0, Math.min(1, confidence)) * 255);
  });
}

/** Pure reference packer used to prove privacy invariants and measure alpha resampling. */
export function packRgbaPixels(
  source: Uint8ClampedArray,
  width: number,
  height: number,
  mask: MaskFrame,
  layout: PackedLayout,
): { geometry: PackedGeometry; pixels: Uint8ClampedArray } {
  if (source.length !== width * height * 4) throw new Error('RGBA source size does not match its dimensions');
  const geometry = packedGeometry(width, height, layout);
  const packed = new Uint8ClampedArray(geometry.packedWidth * geometry.packedHeight * 4);
  const alpha = softAlphaBytes(mask);
  const sampleAlpha = (x: number, y: number): number => {
    const maskX = Math.min(mask.width - 1, Math.max(0, Math.floor((x * mask.width) / width)));
    const maskY = Math.min(mask.height - 1, Math.max(0, Math.floor((y * mask.height) / height)));
    return alpha[maskY * mask.width + maskX] ?? 0;
  };
  const encodedAlpha = new Uint8ClampedArray(geometry.alpha.width * geometry.alpha.height);
  const stripHalfWidth = Math.floor(geometry.alpha.width / 2);
  const stripHalfHeight = geometry.alpha.height * 2;
  if (layout === 'alpha-strip-half') {
    for (let y = 0; y < stripHalfHeight; y += 1) {
      for (let x = 0; x < stripHalfWidth; x += 1) {
        const sourceX = Math.min(width - 1, Math.floor((x * width) / stripHalfWidth));
        const sourceY = Math.min(height - 1, Math.floor((y * height) / stripHalfHeight));
        const atlasX = x + (y >= geometry.alpha.height ? stripHalfWidth : 0);
        const atlasY = y % geometry.alpha.height;
        encodedAlpha[atlasY * geometry.alpha.width + atlasX] = sampleAlpha(sourceX, sourceY);
      }
    }
  } else {
    for (let y = 0; y < geometry.alpha.height; y += 1) {
      for (let x = 0; x < geometry.alpha.width; x += 1) {
        const sourceX = Math.min(width - 1, Math.floor((x * width) / geometry.alpha.width));
        const sourceY = Math.min(height - 1, Math.floor((y * height) / geometry.alpha.height));
        encodedAlpha[y * geometry.alpha.width + x] = sampleAlpha(sourceX, sourceY);
      }
    }
  }
  const decodedAlpha = (x: number, y: number): number => {
    if (layout === 'alpha-strip-half') {
      const halfX = Math.min(stripHalfWidth - 1, Math.floor((x * stripHalfWidth) / width));
      const halfY = Math.min(stripHalfHeight - 1, Math.floor((y * stripHalfHeight) / height));
      const atlasX = halfX + (halfY >= geometry.alpha.height ? stripHalfWidth : 0);
      const atlasY = halfY % geometry.alpha.height;
      return encodedAlpha[atlasY * geometry.alpha.width + atlasX] ?? 0;
    }
    const alphaX = Math.min(geometry.alpha.width - 1, Math.floor((x * geometry.alpha.width) / width));
    const alphaY = Math.min(geometry.alpha.height - 1, Math.floor((y * geometry.alpha.height) / height));
    return encodedAlpha[alphaY * geometry.alpha.width + alphaX] ?? 0;
  };
  const put = (x: number, y: number, red: number, green: number, blue: number): void => {
    const offset = (y * geometry.packedWidth + x) * 4;
    packed[offset] = red;
    packed[offset + 1] = green;
    packed[offset + 2] = blue;
    packed[offset + 3] = 255;
  };
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      // Premultiply against the same representation the receiver decodes. In
      // particular, the quarter-area layout must not retain colour behind an
      // alpha sample that disappeared during reduction.
      const a = decodedAlpha(x, y);
      const sourceOffset = (y * width + x) * 4;
      put(
        geometry.colour.x + x,
        geometry.colour.y + y,
        Math.round(source[sourceOffset]! * a / 255),
        Math.round(source[sourceOffset + 1]! * a / 255),
        Math.round(source[sourceOffset + 2]! * a / 255),
      );
    }
  }
  for (let y = 0; y < geometry.alpha.height; y += 1) {
    for (let x = 0; x < geometry.alpha.width; x += 1) {
      const a = encodedAlpha[y * geometry.alpha.width + x] ?? 0;
      put(geometry.alpha.x + x, geometry.alpha.y + y, a, a, a);
    }
  }
  return { geometry, pixels: packed };
}

/**
 * Deterministic pre-codec reference measurement. It compares the confidence
 * matte with the matte decoded from a packed layout, restricting the score to
 * soft/transition pixels so a large black background cannot flatter it.
 */
export function measureAlphaEdgeRoundTrip(mask: MaskFrame, layout: PackedLayout): AlphaEdgeMeasurement {
  const width = mask.width;
  const height = mask.height;
  const source = new Uint8ClampedArray(width * height * 4);
  const desired = softAlphaBytes(mask);
  const { geometry, pixels } = packRgbaPixels(source, width, height, mask, layout);
  let edgePixels = 0;
  let absoluteError = 0;
  let squaredError = 0;
  const valueAt = (x: number, y: number): number => desired[y * width + x] ?? 0;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const expected = valueAt(x, y);
      const transition = (expected > 0 && expected < 255)
        || (x > 0 && Math.abs(expected - valueAt(x - 1, y)) >= 8)
        || (x + 1 < width && Math.abs(expected - valueAt(x + 1, y)) >= 8)
        || (y > 0 && Math.abs(expected - valueAt(x, y - 1)) >= 8)
        || (y + 1 < height && Math.abs(expected - valueAt(x, y + 1)) >= 8);
      if (!transition) continue;
      const stripHalfWidth = Math.floor(geometry.alpha.width / 2);
      const stripHalfHeight = geometry.alpha.height * 2;
      const halfX = Math.min(stripHalfWidth - 1, Math.floor((x * stripHalfWidth) / width));
      const halfY = Math.min(stripHalfHeight - 1, Math.floor((y * stripHalfHeight) / height));
      const alphaX = layout === 'alpha-strip-half'
        ? geometry.alpha.x + halfX + (halfY >= geometry.alpha.height ? stripHalfWidth : 0)
        : geometry.alpha.x + Math.min(geometry.alpha.width - 1, Math.floor((x * geometry.alpha.width) / width));
      const alphaY = layout === 'alpha-strip-half'
        ? geometry.alpha.y + (halfY % geometry.alpha.height)
        : geometry.alpha.y + Math.min(geometry.alpha.height - 1, Math.floor((y * geometry.alpha.height) / height));
      const decoded = pixels[(alphaY * geometry.packedWidth + alphaX) * 4] ?? 0;
      const error = decoded - expected;
      edgePixels += 1;
      absoluteError += Math.abs(error);
      squaredError += error * error;
    }
  }
  const meanSquaredError = edgePixels > 0 ? squaredError / edgePixels : 0;
  return {
    layout,
    encodedAlphaPixels: geometry.alpha.width * geometry.alpha.height,
    edgePixels,
    edgeMeanAbsoluteError: edgePixels > 0 ? absoluteError / edgePixels : 0,
    edgePsnrDb: meanSquaredError === 0 ? Number.POSITIVE_INFINITY : 10 * Math.log10((255 * 255) / meanSquaredError),
  };
}

export class PackedFrameCanvas {
  readonly canvas = document.createElement('canvas');
  readonly colourCanvas = document.createElement('canvas');
  readonly maskCanvas = document.createElement('canvas');
  readonly reducedMaskCanvas = document.createElement('canvas');
  readonly encodedMaskCanvas = document.createElement('canvas');
  readonly sourceMaskCanvas = document.createElement('canvas');

  #colour = this.colourCanvas.getContext('2d', { alpha: true })!;
  #mask = this.maskCanvas.getContext('2d', { alpha: true })!;
  #reducedMask = this.reducedMaskCanvas.getContext('2d', { alpha: true })!;
  #encodedMask = this.encodedMaskCanvas.getContext('2d', { alpha: true })!;
  #sourceMask = this.sourceMaskCanvas.getContext('2d', { alpha: true })!;
  #packed = this.canvas.getContext('2d', { alpha: false })!;
  #maskImage?: ImageData;
  #layout: PackedLayout;
  #sourceWidth = 2;
  #sourceHeight = 2;
  #geometry: PackedGeometry;

  constructor(layout: PackedLayout = 'side-by-side') {
    this.#layout = layout;
    this.#geometry = packedGeometry(2, 2, layout);
  }

  get layout(): PackedLayout {
    return this.#layout;
  }

  get geometry(): PackedGeometry {
    return this.#geometry;
  }

  setLayout(layout: PackedLayout): void {
    if (!PACKED_LAYOUTS.includes(layout)) return;
    this.#layout = layout;
    this.configure(this.#sourceWidth, this.#sourceHeight);
    this.clear();
  }

  configure(sourceWidth: number, sourceHeight: number, maxColourWidth = 640): void {
    const safeSourceWidth = Number.isFinite(sourceWidth) && sourceWidth > 0 ? sourceWidth : 2;
    const safeSourceHeight = Number.isFinite(sourceHeight) && sourceHeight > 0 ? sourceHeight : 2;
    const width = Math.max(2, Math.min(maxColourWidth, safeSourceWidth));
    const height = Math.max(2, Math.round((width * safeSourceHeight) / safeSourceWidth));
    this.#sourceWidth = safeSourceWidth;
    this.#sourceHeight = safeSourceHeight;
    const geometry = packedGeometry(width, height, this.#layout);
    if (this.canvas.width === geometry.packedWidth && this.canvas.height === geometry.packedHeight) {
      this.#geometry = geometry;
      return;
    }
    this.#geometry = geometry;
    this.colourCanvas.width = this.maskCanvas.width = width;
    this.colourCanvas.height = this.maskCanvas.height = height;
    this.canvas.width = geometry.packedWidth;
    this.canvas.height = geometry.packedHeight;
    this.reducedMaskCanvas.width = this.#layout === 'alpha-strip-half' ? geometry.alpha.width / 2 : geometry.alpha.width;
    this.reducedMaskCanvas.height = this.#layout === 'alpha-strip-half' ? geometry.alpha.height * 2 : geometry.alpha.height;
    this.encodedMaskCanvas.width = geometry.alpha.width;
    this.encodedMaskCanvas.height = geometry.alpha.height;
  }

  clear(): void {
    this.#colour.clearRect(0, 0, this.colourCanvas.width, this.colourCanvas.height);
    this.#mask.clearRect(0, 0, this.maskCanvas.width, this.maskCanvas.height);
    this.#reducedMask.clearRect(0, 0, this.reducedMaskCanvas.width, this.reducedMaskCanvas.height);
    this.#encodedMask.clearRect(0, 0, this.encodedMaskCanvas.width, this.encodedMaskCanvas.height);
    this.#packed.fillStyle = '#000';
    this.#packed.fillRect(0, 0, this.canvas.width, this.canvas.height);
  }

  draw(video: CanvasImageSource, mask: MaskFrame, trackedBody: NormalizedBox): void {
    this.configure((video as HTMLVideoElement).videoWidth || this.colourCanvas.width, (video as HTMLVideoElement).videoHeight || this.colourCanvas.height);
    if (!this.#maskImage || this.sourceMaskCanvas.width !== mask.width || this.sourceMaskCanvas.height !== mask.height) {
      this.sourceMaskCanvas.width = mask.width;
      this.sourceMaskCanvas.height = mask.height;
      this.#maskImage = this.#sourceMask.createImageData(mask.width, mask.height);
    }
    const alpha = softAlphaBytes(mask);
    const pixels = this.#maskImage!.data;
    for (let index = 0; index < alpha.length; index += 1) {
      const value = alpha[index]!;
      const offset = index * 4;
      // White with soft canvas alpha composites to confidence-valued luma over black,
      // while destination-in uses that same soft alpha for the colour matte.
      pixels[offset] = pixels[offset + 1] = pixels[offset + 2] = 255;
      pixels[offset + 3] = value;
    }
    this.#sourceMask.putImageData(this.#maskImage!, 0, 0);

    const width = this.colourCanvas.width;
    const height = this.colourCanvas.height;
    this.#mask.globalCompositeOperation = 'source-over';
    this.#mask.clearRect(0, 0, width, height);
    this.#mask.save();
    this.#mask.beginPath();
    this.#mask.rect(
      trackedBody.left * width,
      trackedBody.top * height,
      (trackedBody.right - trackedBody.left) * width,
      (trackedBody.bottom - trackedBody.top) * height,
    );
    this.#mask.clip();
    this.#mask.drawImage(this.sourceMaskCanvas, 0, 0, width, height);
    this.#mask.restore();

    // Reduce once into the exact on-wire alpha raster, then reconstruct the
    // colour matte from that raster. This couples colour and decoded alpha so
    // codec/layout reduction cannot leave visible RGB behind a zero alpha.
    this.#encodedMask.clearRect(0, 0, this.encodedMaskCanvas.width, this.encodedMaskCanvas.height);
    if (this.#layout === 'alpha-strip-half') {
      const halfWidth = this.reducedMaskCanvas.width;
      const tileHeight = this.encodedMaskCanvas.height;
      this.#reducedMask.clearRect(0, 0, halfWidth, tileHeight * 2);
      this.#reducedMask.imageSmoothingEnabled = true;
      this.#reducedMask.drawImage(this.maskCanvas, 0, 0, halfWidth, tileHeight * 2);
      this.#encodedMask.imageSmoothingEnabled = false;
      this.#encodedMask.drawImage(
        this.reducedMaskCanvas,
        0, 0, halfWidth, tileHeight,
        0, 0, halfWidth, tileHeight,
      );
      this.#encodedMask.drawImage(
        this.reducedMaskCanvas,
        0, tileHeight, halfWidth, tileHeight,
        halfWidth, 0, halfWidth, tileHeight,
      );
      this.#reducedMask.clearRect(0, 0, halfWidth, tileHeight * 2);
      this.#reducedMask.imageSmoothingEnabled = false;
      this.#reducedMask.drawImage(
        this.encodedMaskCanvas,
        0, 0, halfWidth, tileHeight,
        0, 0, halfWidth, tileHeight,
      );
      this.#reducedMask.drawImage(
        this.encodedMaskCanvas,
        halfWidth, 0, halfWidth, tileHeight,
        0, tileHeight, halfWidth, tileHeight,
      );
      this.#mask.clearRect(0, 0, width, height);
      this.#mask.imageSmoothingEnabled = true;
      this.#mask.drawImage(this.reducedMaskCanvas, 0, 0, width, height);
    } else {
      this.#encodedMask.imageSmoothingEnabled = true;
      this.#encodedMask.drawImage(
        this.maskCanvas,
        0,
        0,
        this.encodedMaskCanvas.width,
        this.encodedMaskCanvas.height,
      );
      this.#mask.clearRect(0, 0, width, height);
      this.#mask.drawImage(this.encodedMaskCanvas, 0, 0, width, height);
    }

    this.#colour.globalCompositeOperation = 'source-over';
    this.#colour.clearRect(0, 0, width, height);
    this.#colour.drawImage(video, 0, 0, width, height);
    this.#colour.globalCompositeOperation = 'destination-in';
    this.#colour.drawImage(this.maskCanvas, 0, 0);
    this.#colour.globalCompositeOperation = 'source-over';

    this.#packed.fillStyle = '#000';
    this.#packed.fillRect(0, 0, this.canvas.width, this.canvas.height);
    this.#packed.drawImage(this.colourCanvas, this.#geometry.colour.x, this.#geometry.colour.y);
    this.#packed.drawImage(
      this.encodedMaskCanvas,
      this.#geometry.alpha.x,
      this.#geometry.alpha.y,
      this.#geometry.alpha.width,
      this.#geometry.alpha.height,
    );
  }
}
